/**
 * Cliente de conversación del Agente CEO.
 *
 * Habla con la Edge Function `ia-direccion-chat` por `fetch` + SSE, como manda
 * la sección 4.1 de la spec: `supabase.functions.invoke` no expone el cuerpo
 * como stream, y sin stream el primer token tardaría lo que tarde la respuesta
 * completa (criterio 9 de aceptación: < 3 s).
 *
 * Lo que se ve en pantalla mientras escribe vive aquí; lo persistido lo lee
 * `useMensajes`. Al terminar un turno se invalida esa consulta para que la
 * fuente vuelva a ser la base de datos.
 */
import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/lib/config";

const URL_FUNCION = `${SUPABASE_URL}/functions/v1/ia-direccion-chat`;

export type ModoAgente = "rapido" | "profundo";

export interface HerramientaUsada {
  nombre: string;
  argumentos?: Record<string, unknown>;
  ms?: number;
  error?: string | null;
}

export interface PropuestaMemoria {
  tipo: "memoria";
  id: number;
  estado: string;
  contenido: string;
  alcance: "global" | "usuario";
  duplicada?: boolean;
}

export interface TurnoEnVivo {
  texto: string;
  estado: string | null;
  herramientas: HerramientaUsada[];
  propuestas: PropuestaMemoria[];
  idMensaje: string | null;
  memoriasCitadas: number[];
}

const TURNO_VACIO: TurnoEnVivo = {
  texto: "", estado: null, herramientas: [], propuestas: [], idMensaje: null, memoriasCitadas: [],
};

export interface ContextoPantalla {
  ruta: string;
  titulo?: string;
  filtros?: Record<string, unknown>;
}

export function useChatAgenteCeo() {
  const qc = useQueryClient();
  const [idConversacion, setIdConversacion] = useState<string | null>(null);
  /**
   * Borrador: hay una conversación empezándose que todavía no existe en la base.
   *
   * Sin esto, "Nueva conversación" no hacía nada visible: limpiaba el estado y
   * la pantalla de entrada —que se dibuja justamente cuando no hay
   * `idConversacion`— se volvía a pintar. El botón te devolvía al mismo lugar.
   * La conversación sigue naciendo en el servidor con el primer envío; esto solo
   * le da al front un estado para "ya elegí, déjame escribir".
   */
  const [enBorrador, setEnBorrador] = useState(false);
  const [turno, setTurno] = useState<TurnoEnVivo>(TURNO_VACIO);
  const [enVuelo, setEnVuelo] = useState(false);
  const [error, setError] = useState<{ codigo: string; mensaje: string } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  /**
   * Turno vigente. Cada envío y cada cambio de conversación lo incrementa, y un
   * stream solo escribe en el estado mientras siga siendo el vigente.
   *
   * Sin esto, salir de una conversación mientras el agente respondía —"Nueva
   * conversación", otra de la lista o la flecha del drawer— dejaba el texto en
   * vivo cayendo en la pantalla nueva, y al terminar se quedaba ahí pegado: su
   * `idMensaje` no está entre los guardados de la otra conversación.
   */
  const generacionRef = useRef(0);

  const detener = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setEnVuelo(false);
  }, []);

  const correr = useCallback(
    async (cuerpo: Record<string, unknown>) => {
      setError(null);
      setTurno(TURNO_VACIO);
      setEnVuelo(true);

      const generacion = ++generacionRef.current;
      const vigente = () => generacionRef.current === generacion;
      const control = new AbortController();
      abortRef.current = control;

      try {
        const { data: sesion } = await supabase.auth.getSession();
        const token = sesion.session?.access_token;
        if (!token) {
          if (vigente()) setError({ codigo: "sin_permiso", mensaje: "Tu sesión expiró. Vuelve a entrar." });
          return;
        }

        const res = await fetch(URL_FUNCION, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            apikey: SUPABASE_PUBLISHABLE_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(cuerpo),
          signal: control.signal,
        });

        if (!res.ok || !res.body) {
          if (vigente()) setError({ codigo: "interno", mensaje: "No pude contactar al agente. Intenta de nuevo." });
          return;
        }

        const lector = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let idNuevo: string | null = null;

        while (true) {
          const { done, value } = await lector.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const partes = buffer.split("\n\n");
          buffer = partes.pop() ?? "";

          for (const parte of partes) {
            // `: ping` cada 15 s mantiene viva la conexión; no es un evento.
            if (parte.startsWith(":")) continue;
            const evento = parte.split("\n").find((l) => l.startsWith("event: "))?.slice(7).trim();
            const datos = parte.split("\n").find((l) => l.startsWith("data: "))?.slice(6);
            if (!evento || !datos) continue;

            let payload: any;
            try { payload = JSON.parse(datos); } catch { continue; }

            // Un turno que el usuario dejó atrás se sigue leyendo hasta el final
            // —la función termina y guarda la respuesta en su conversación—,
            // pero ya no toca la pantalla.
            if (!vigente()) continue;

            switch (evento) {
              case "conversacion":
                // El servidor la creó y aquí se adopta: de borrador a real, sin
                // que el usuario note el cambio.
                idNuevo = payload.id_conversacion;
                setIdConversacion(payload.id_conversacion);
                setEnBorrador(false);
                break;
              case "estado":
                setTurno((t) => ({ ...t, estado: payload.texto }));
                break;
              case "delta":
                setTurno((t) => ({ ...t, texto: t.texto + payload.texto, estado: null }));
                break;
              case "propuesta":
                setTurno((t) => ({ ...t, propuestas: [...t.propuestas, payload] }));
                break;
              case "fin":
                setTurno((t) => ({
                  ...t,
                  estado: null,
                  idMensaje: payload.id_mensaje ?? null,
                  herramientas: payload.herramientas ?? [],
                  memoriasCitadas: payload.memorias_citadas ?? [],
                }));
                break;
              case "error":
                setError({ codigo: payload.codigo, mensaje: payload.mensaje });
                break;
            }
          }
        }

        const id = idNuevo ?? (cuerpo.id_conversacion as string | undefined) ?? null;
        if (id) {
          qc.invalidateQueries({ queryKey: ["ia-mensajes", id] });
          qc.invalidateQueries({ queryKey: ["ia-conversaciones"] });
          qc.invalidateQueries({ queryKey: ["ia-memorias"] });
          // El título lo escribe la función DESPUÉS de cerrar el stream, en
          // segundo plano. Si solo refrescamos aquí, la lista se queda con el
          // título en null y lo cachea: por eso se veía "Sin título" en una
          // conversación que en la base ya tenía nombre. Dos repasos tardíos.
          for (const ms of [3000, 10000]) {
            setTimeout(() => qc.invalidateQueries({ queryKey: ["ia-conversaciones"] }), ms);
          }
        }
      } catch (e) {
        // Abortar es una acción del usuario (botón Detener), no un error.
        if ((e as Error)?.name !== "AbortError" && vigente()) {
          setError({ codigo: "interno", mensaje: "Se interrumpió la respuesta. Intenta de nuevo." });
        }
      } finally {
        if (vigente()) setEnVuelo(false);
        if (abortRef.current === control) abortRef.current = null;
      }
    },
    [qc],
  );

  /**
   * Pide el brief. **Solo cuando el usuario lo pide**: abrir la pantalla ya no
   * lo dispara. Hasta la v7 se llamaba al montar, y el historial acabó con 21 de
   * 25 conversaciones sin una sola pregunta real.
   */
  const pedirBrief = useCallback(
    (o: { contextoPantalla?: ContextoPantalla | null; usarBorrador?: boolean } = {}) => {
      setIdConversacion(null);
      setEnBorrador(true);
      return correr({
        accion: "iniciar",
        contexto_pantalla: o.contextoPantalla ?? null,
        usar_borrador: !!o.usarBorrador,
      });
    },
    [correr],
  );

  /**
   * Manda una pregunta. Si todavía no hay conversación, el servidor la crea con
   * esta pregunta: la conversación nace del primer mensaje, no de abrir la
   * pantalla.
   */
  const enviar = useCallback(
    (texto: string, o: { modo?: ModoAgente; contextoPantalla?: ContextoPantalla | null; nueva?: boolean } = {}) => {
      // `nueva`: repetir una pregunta abre conversación propia (datos de hoy, sin arrastrar la
      // charla anterior, que se queda en el historial). Nace en el servidor con este envío.
      if (o.nueva) {
        setIdConversacion(null);
        setEnBorrador(true);
      }
      return correr({
        accion: "mensaje",
        id_conversacion: o.nueva ? null : idConversacion ?? null,
        texto,
        modo: o.modo ?? "rapido",
        contexto_pantalla: o.contextoPantalla ?? null,
      });
    },
    [correr, idConversacion],
  );

  /**
   * Suelta el turno en curso al cambiar de conversación. No lo aborta: la
   * respuesta se termina de escribir y queda guardada donde se preguntó; solo
   * deja de pintarse aquí, y el composer queda libre para la conversación nueva.
   */
  const soltarTurno = useCallback(() => {
    generacionRef.current++;
    abortRef.current = null;
    setEnVuelo(false);
  }, []);

  const abrirExistente = useCallback((id: string) => {
    soltarTurno();
    setIdConversacion(id);
    setEnBorrador(false);
    setTurno(TURNO_VACIO);
    setError(null);
  }, [soltarTurno]);

  /**
   * "Nueva conversación": entra al borrador. No llama a la función ni crea
   * filas; solo deja el composer vacío y listo para escribir.
   */
  const nueva = useCallback(() => {
    soltarTurno();
    setIdConversacion(null);
    setEnBorrador(true);
    setTurno(TURNO_VACIO);
    setError(null);
  }, [soltarTurno]);

  /** Vuelve a la pantalla de entrada sin abrir nada. */
  const limpiar = useCallback(() => {
    soltarTurno();
    setIdConversacion(null);
    setEnBorrador(false);
    setTurno(TURNO_VACIO);
    setError(null);
  }, [soltarTurno]);

  /** Lo que decide si se dibuja la conversación o la pantalla de entrada. */
  const hayConversacion = !!idConversacion || enBorrador || enVuelo || !!turno.texto;

  return {
    idConversacion, enBorrador, hayConversacion, turno, enVuelo, error,
    pedirBrief, enviar, detener, abrirExistente, nueva, limpiar,
  };
}
